"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.DismissiblePill = void 0;
require("./DismissiblePill.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _renderObject = require("../../util/renderObject/renderObject");
var _Button = _interopRequireDefault(require("../Button/Button"));
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Image = _interopRequireDefault(require("../Image/Image"));
var _Link = _interopRequireDefault(require("../Link/Link"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * DismissiblePill component
 *
 * @method
 * @param { DismissiblePillProps } props
 * @returns DismissiblePill
 */

const DismissiblePill = props => {
  const {
    actionType = defaultProps.actionType,
    active = defaultProps.active,
    ariaDescribedBy,
    children,
    disabled = defaultProps.disabled,
    externalPillAction,
    externalMedia,
    message,
    ref,
    screenReaderText,
    swatchImage,
    tabIndex,
    unavailable = defaultProps.unavailable
  } = props;
  const isButton = actionType === 'button';
  const isLink = actionType === 'link';
  const pillContent = /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, externalMedia && (0, _renderObject.renderObject)(externalMedia), !externalMedia && swatchImage?.imageUrl && /*#__PURE__*/_react.default.createElement(_Image.default, {
    ariaHidden: true,
    desktopImage: swatchImage.imageUrl
  }), (children || message) && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-DismissiblePill--contentContainer"
  }, children && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-DismissiblePill--children"
  }, children), message && /*#__PURE__*/_react.default.createElement(_Text.default, {
    className: "pal-c-DismissiblePill--text",
    htmlTag: "span",
    textStyle: "label"
  }, message)), /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: "pal-c-DismissiblePill--dismissibleIcon",
    name: 'x'
  }));
  const conditionalPropsButton = (0, _conditionalAttributes.conditionalAttributes)({
    'aria-describedby': ariaDescribedBy || undefined,
    'aria-disabled': disabled || undefined,
    'aria-pressed': active || undefined
  });
  const conditionalPropsLink = (0, _conditionalAttributes.conditionalAttributes)({
    'aria-current': active ? 'true' : 'false',
    'aria-describedby': ariaDescribedBy || undefined,
    'aria-disabled': disabled || undefined
  });
  const componentClassName = (0, _classnames.default)('pal-c-DismissiblePill', {
    'pal-c-DismissiblePill--active': active && !disabled,
    'pal-c-DismissiblePill--disabled': disabled,
    'pal-c-DismissiblePill--unavailable': unavailable
  });
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, isButton && /*#__PURE__*/_react.default.createElement(_Button.default, _extends({
    buttonRef: ref,
    className: componentClassName,
    disabled: disabled,
    onClick: externalPillAction,
    screenReaderText: screenReaderText,
    tabIndex: tabIndex,
    variant: "unstyled"
  }, conditionalPropsButton), pillContent), isLink && /*#__PURE__*/_react.default.createElement(_Link.default, _extends({
    linkRef: ref,
    className: componentClassName,
    disabled: disabled,
    onClick: externalPillAction,
    screenReaderText: screenReaderText,
    tabIndex: tabIndex,
    variant: "unstyled"
  }, conditionalPropsLink), pillContent));
};

/**
 * Property type definitions
 * @typedef DismissiblePillProps
 * @type {object}
 * @property {string} actionType - Sets the type of action ( button or link )
 * @property {boolean} active - Sets the selected state for button/link checked state
 * @property {string} ariaDescribedBy - sets the aria-describedby attribute for button or input elements
 * @property {boolean} disabled - Sets the disabled state
 * @property {func} externalPillAction - Sets the onClick for button or link
 * @property {string} message - Sets the message to be displayed in the pill
 * @property {any} ref - Sets the ref of the dismissible pull
 * @property {string} screenReaderText - Sets the screenReaderText for the pill
 * @property {number} tabIndex - Sets the tabIndex for the button or input elements
 * @property {boolean} unavailable - Sets the unavailable state for button/link checked state
 */
exports.DismissiblePill = DismissiblePill;
const propTypes = exports.propTypes = {
  /**
   * Sets the type of action ( button or link )
   */
  actionType: _propTypes.default.oneOf(['button', 'link']),
  /**
   * Sets the active state for button/link checked state
   */
  active: _propTypes.default.bool,
  /**
   * Sets the aria-describedby attribute for button or input elements
   */
  ariaDescribedBy: _propTypes.default.string,
  /**
   * Optional children to be rendered before message.
  */
  children: _propTypes.default.node,
  /**
   * Sets the disabled state
   */
  disabled: _propTypes.default.bool,
  /**
   * Sets the onClick for button or link
   */
  externalPillAction: _propTypes.default.func,
  /**
   * Sets the message to be displayed in the pill
   */
  message: _propTypes.default.string,
  /**
   * pill reference
   */
  ref: _propTypes.default.any,
  /**
   * Sets the screenReaderText for the pill
   */
  screenReaderText: _propTypes.default.string,
  /**
   * Sets the tab-index of the button or input element
   */
  tabIndex: _propTypes.default.number,
  /**
   * Sets the unavailable state for button/link checked state
   */
  unavailable: _propTypes.default.bool
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} active - active: false - default active state of the pill
 * @property {string} actionType - actionType: 'button' - default action type of the pill
 * @property {boolean} disabled - disabled: false - default disabled state of the pill
 * @property {boolean} unavailable - unavailable: false - default unavailable state of the pill
 */
const defaultProps = exports.defaultProps = {
  active: false,
  actionType: 'button',
  disabled: false,
  unavailable: false
};
DismissiblePill.propTypes = propTypes;
var _default = exports.default = DismissiblePill;